import { useCallback, useEffect, useState } from 'react';
import {
  artifactService,
  composeArtifactTitle,
  type Artifact,
  type MessageArtifact,
} from '@ideoz/core';
import { renderArtifactHtml, showToast } from '@ideoz/ui';
import type { ArtifactsOwner } from './artifacts.types';
import { asMessageArtifact } from './artifactMappers';
import { resolveArtifact } from './renderers/registry';

/**
 * Logic for the Artifacts module: load / delete / edit / share artifacts for
 * a given owner, plus the "active artifact" side-panel state. Presentational
 * components consume this hook's output as props.
 */
export function useArtifacts(owner: ArtifactsOwner) {
  const ownerId = owner.type === 'scope' ? owner.scopeId : owner.startupId;

  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeArtifact, setActiveArtifact] = useState<MessageArtifact | null>(null);

  const fetchArtifacts = useCallback(async () => {
    if (!ownerId) return;
    try {
      const list =
        owner.type === 'scope'
          ? await artifactService.listScopeArtifacts(ownerId)
          : await artifactService.listStartupArtifacts(ownerId);
      setArtifacts(list);
    } catch (err) {
      console.error('Failed to load artifacts', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!ownerId) return;
      setIsLoading(true);
      try {
        await fetchArtifacts();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerId, fetchArtifacts]);

  const openArtifact = useCallback((row: Artifact) => {
    setActiveArtifact(asMessageArtifact(row));
  }, []);

  const closeArtifact = useCallback(() => setActiveArtifact(null), []);

  const deleteArtifact = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await artifactService.deleteArtifact(id);
      setArtifacts((prev) => prev.filter((a) => a.id !== id));
      showToast({ message: 'Artifact deleted', severity: 'success' });
    } catch (err) {
      showToast({ message: 'Failed to delete artifact', severity: 'error' });
      console.error(err);
    } finally {
      setDeletingId(null);
    }
  }, []);

  const saveActiveArtifact = useCallback(
    async (content: string) => {
      const id = activeArtifact?.id;
      if (!id) return;
      try {
        const updated = await artifactService.updateArtifact(id, { content });
        // Keep the panel + the row in the list in sync.
        setArtifacts((prev) =>
          prev.map((r) => (r.id === id ? { ...r, content: updated.content ?? content } : r)),
        );
        setActiveArtifact((prev) =>
          prev ? { ...prev, content: updated.content ?? content } : prev,
        );
        showToast({ message: 'Artifact updated', severity: 'success' });
      } catch (err) {
        console.error('Failed to update artifact', err);
        showToast({ message: 'Failed to save artifact', severity: 'error' });
        throw err;
      }
    },
    [activeArtifact?.id],
  );

  const saveStructuredActiveArtifact = useCallback(
    async (json: Record<string, unknown>) => {
      const id = activeArtifact?.id;
      if (!id) return;
      try {
        // Manual edit → PUT updates the artifact in place.
        const updated = await artifactService.updateArtifact(id, { structured_content: json });
        const nextStructured = updated.structured_content ?? json;
        // Keep the list row + open panel in sync with the edit.
        setArtifacts((prev) =>
          prev.map((r) =>
            r.id === id
              ? { ...r, structured_content: nextStructured, content: updated.content ?? r.content }
              : r
          )
        );
        setActiveArtifact((prev) =>
          prev
            ? { ...prev, structuredContent: nextStructured, content: updated.content ?? prev.content }
            : prev
        );
        showToast({ message: 'Artifact updated', severity: 'success' });
      } catch (err) {
        console.error('Failed to update artifact', err);
        showToast({ message: 'Failed to save artifact', severity: 'error' });
        throw err;
      }
    },
    [activeArtifact?.id]
  );

  const shareActiveArtifact = useCallback(async () => {
    const id = activeArtifact?.id;
    if (!id || !activeArtifact) return;
    try {
      // Predefined types (persona, JTBD, 5 Whys, HMW, Atlas…) publish their
      // designed HTML card via the renderer; generic artifacts (no renderer)
      // publish their markdown rendered to HTML. Both end up as a self-contained
      // HTML document on S3.
      const view = activeArtifact.structuredContent
        ? resolveArtifact(activeArtifact.structuredContent, activeArtifact.name)
        : null;
      const html = view
        ? view.renderer.toHtml(
            view.model,
            composeArtifactTitle(activeArtifact.structuredContent) ?? activeArtifact.name
          )
        : renderArtifactHtml(activeArtifact.content ?? '', activeArtifact.name);
      const updated = await artifactService.publishArtifactToS3(id, html);
      const demoUrl = updated.demo_url ?? activeArtifact.demoUrl ?? null;
      if (!demoUrl) throw new Error('Publish returned no URL');
      setArtifacts((prev) => prev.map((r) => (r.id === id ? { ...r, demo_url: demoUrl } : r)));
      setActiveArtifact((prev) => (prev ? { ...prev, demoUrl } : prev));
      await navigator.clipboard.writeText(demoUrl);
      showToast({ message: 'Share link copied to clipboard', severity: 'success' });
    } catch (err) {
      console.error('Failed to share artifact', err);
      showToast({ message: 'Failed to create share link', severity: 'error' });
      throw err;
    }
  }, [activeArtifact]);

  return {
    artifacts,
    isLoading,
    deletingId,
    activeArtifact,
    openArtifact,
    closeArtifact,
    deleteArtifact,
    saveActiveArtifact,
    saveStructuredActiveArtifact,
    shareActiveArtifact,
  };
}
